import { type ContractMenuItem } from "@/components/fragments/contract/contract-menu";
import { type ShipmentPlan } from "@/components/fragments/plan/type";
import { formatAsiaTokyoDateToString } from "@/modules/utils/date";
import { isNonNullable } from "@/modules/utils/predicate";
import { type NextRouter } from "next/router";
import { type GetAllDataPagesPlansHistoryQuery } from "./__gql__/get-all-data.gen";
import { getViewableShipmentPlans, type Shipment } from "@/components/fragments/plan";
import { pagesPath } from "@/pathpida/generated/$path";

export const getContractIdFromQuery = ({ isReady, query: { "contract-id": contractId } }: NextRouter): string => {
  return isReady && typeof contractId === "string" ? contractId : "";
};

export const getViewableShipmentPlansByContractedPlan = (
  selectedContractedPlanUuid: string | undefined,
  shipments: GetAllDataPagesPlansHistoryQuery["shipments"] | undefined,
): ShipmentPlan[] | undefined => {
  return getViewableShipmentPlans(getShipments(selectedContractedPlanUuid, shipments), {
    getPlanDetailHref: (shipmentId) => pagesPath.plans.detail._shipmentId(shipmentId).$url(),
  });
};

export const getContractMenuItem = (
  selectedContractedPlanUuid: string | undefined,
  familyCustomerContractedPlans: GetAllDataPagesPlansHistoryQuery["familyCustomer"]["familyCustomerContractedPlans"],
): ContractMenuItem | undefined => {
  return familyCustomerContractedPlans?.find(({ uuid }) => uuid === selectedContractedPlanUuid);
};

export const getYears = (shipments: ShipmentPlan[] | undefined): string[] => {
  const years = shipments
    ?.map((shipment) => formatAsiaTokyoDateToString(shipment.shippedAt, "yyyy年"))
    .filter(isNonNullable);

  return [...new Set(years)];
};

const getShipments = (
  selectedContractedPlanUuid: string | undefined,
  shipments: GetAllDataPagesPlansHistoryQuery["shipments"] | undefined,
): Shipment[] | undefined => {
  if (selectedContractedPlanUuid === undefined && shipments === undefined) {
    return undefined;
  }

  return shipments?.find(
    ({ familyCustomerContractedPlanUuid }) => familyCustomerContractedPlanUuid === selectedContractedPlanUuid,
  )?.shipments;
};

export const getLatestShipmentYear = (shipments: ShipmentPlan[] | undefined): string | undefined => {
  const years = getYears(shipments);
  if (years.length < 0 || years[0] === undefined) {
    return undefined;
  }

  return years[0];
};
